import { stringify } from 'qs';
import IApiResponse from '../../../interfaces/IApiResponse';
import {
    getApiErrorType,
    getRoundManagerApiAxiosInstance,
} from '../../axios-config';
import i18n from '../../../../lib/i18n';
import IOffersPagination from '../../../interfaces/offers/IOffersPagination';
import IPagination from '../../../interfaces/IPagination';
import IWonOfferResponse from '../../../interfaces/offers/IWonOfferResponse';
import ILoadMaterialResponse from '../../../interfaces/loads/ILoadMaterialResponse';
import IWonFiltersModel from '../../../interfaces/IFiltersModel';
import ILoadsLocationModel from '../../../interfaces/loads/ILoadsLocationModel';
import ILoadsLocationResponse from '../../../interfaces/loads/ILoadsLocationResponse';
import IRoundInfoResponse from '../../../interfaces/offers/IRoundInfoResponse';
import IOffersAvailableV2Response from '../../../interfaces/offers/IOffersAvailableV2Response';
import Position from '../../../enums/position';

function buildOffersRequestParams(
    page: number,
    pageSize: number,
    filters: IWonFiltersModel,
    additionalParams: Record<string, any> = {},
) {
    return {
        _page: page,
        _size: pageSize,
        originFederativeUnit: filters?.originFederativeUnit,
        originLocations: filters?.originLocations,
        destinationFederativeUnit: filters?.destinationFederativeUnit,
        destinationLocations: filters?.destinationLocations,
        channels: filters?.channels,
        initialDate: filters?.initialCollectionDate,
        endingDate: filters?.endingCollectionDate,
        loadId: filters?.loadId,
        materialId: filters?.product,
        dailyLoadOfferStatusFilter: filters?.loadStatus,
        ...additionalParams,
    };
}

function handleOffersApiError(error: any): IApiResponse<any> {
    const errorCause: string = getApiErrorType(error);
    return {
        error: new Error((error as { message: string }).message, {
            cause: errorCause,
        }),
    };
}

export const getOffersAvailableAsync = async (
    filters: IWonFiltersModel,
    page: number,
    pageSize: number = 30,
): Promise<IApiResponse<IOffersPagination>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();
        const request = buildOffersRequestParams(page, pageSize, filters);

        const response = await Api.get('v1/rounds/loads:available', {
            params: request,
            paramsSerializer(params: any) {
                return stringify(params, { allowDots: true });
            },
        });

        if (response.data) {
            return { response: response.data };
        }

        return { error: new Error(i18n.t('ErrorGettingOffers')) };
    } catch (error: any) {
        return handleOffersApiError(error);
    }
};

export const acceptOfferAsync = async (
    offerId: string,
    vehicleName: string,
): Promise<IApiResponse<boolean>> => {
    return processOffersAcceptancesAsync(
        { offerIds: [offerId], vehicleName },
        'accept',
    );
};

export const cancelOfferAcceptAsync = async (
    offerId: string,
): Promise<IApiResponse<boolean>> => {
    return processOffersAcceptancesAsync(
        { offerIds: [offerId] },
        'cancel-accept',
    );
};

export const processOffersAcceptancesAsync = async (
    offers: { offerIds: string[]; vehicleName?: string },
    type: 'accept' | 'cancel-accept',
): Promise<IApiResponse<boolean>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        await Api.post(`v1/rounds/loads:${type}`, {
            dailyLoadOfferIds: offers.offerIds,
            ...(offers.vehicleName !== undefined && {
                vehicleName: offers.vehicleName,
            }),
        });

        return { response: true };
    } catch (error: any) {
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

export const confirmOfferAsync = async (
    offerId: string,
): Promise<IApiResponse<boolean, IConfirmationConflictResponse>> => {
    return processOffersConfirmationsAsync([offerId]);
};

export interface IConfirmationConflictResponse {
    conflictingDailyLoadOfferIds: string[];
}

export const processOffersConfirmationsAsync = async (
    offersIds: string[],
): Promise<IApiResponse<boolean, IConfirmationConflictResponse>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        await Api.post(`v1/rounds/loads:confirm`, {
            dailyLoadOfferIds: offersIds,
        });

        return { response: true };
    } catch (error: any) {
        if (error?.response?.status === 409) {
            return {
                error: new Error('OFFER_CONFLICT', { cause: 'CONFLICT' }),
                conflictData: error.response
                    .data as IConfirmationConflictResponse,
            };
        }
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

export const GetWonOffersPaginated = async (
    filters: IWonFiltersModel,
    page: number = 1,
    pageSize: number = 30,
): Promise<IApiResponse<IPagination<IWonOfferResponse>>> => {
    const request = getOfferRequest(page, pageSize, filters);

    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/rounds/loads:winner`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return { response: response.data };
    } catch (error: any) {
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

function getOfferRequest(
    page: number,
    pageSize: number,
    filters?: IWonFiltersModel,
) {
    return {
        _page: page,
        _size: pageSize,
        loadId: filters?.loadId,
        dailyLoadOfferWonStatus: filters?.loadStatus,
        channels: filters?.channels,
        initialDate: filters?.initialCollectionDate,
        endingDate: filters?.endingCollectionDate,
        originFederativeUnit: filters?.originFederativeUnit,
        originLocations: filters?.originLocations,
        destinationFederativeUnit: filters?.destinationFederativeUnit,
        destinationLocations: filters?.destinationLocations,
        materialId: filters?.product,
    };
}

export const GetWonOfferById = async (
    offerId: string,
): Promise<IApiResponse<IWonOfferResponse>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/rounds/loads/${offerId}`);
        return { response: response.data };
    } catch (error: any) {
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

export const GetMaterials = async (
    name?: string,
    _page: number = 1,
    _size: number = 10,
    closedOffers: boolean = true,
): Promise<IApiResponse<IPagination<ILoadMaterialResponse>>> => {
    const request = {
        name,
        _page,
        _size,
        closedOffers,
    };

    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/rounds/material:paginated`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return { response: response.data };
    } catch {
        return {
            error: new Error(i18n.t('SmartCargas.WonOffers.GetMaterialsError')),
        };
    }
};

export const GetLocations = async (
    filters: ILoadsLocationModel,
    _page: number = 1,
    _size: number = 100,
): Promise<IApiResponse<IPagination<ILoadsLocationResponse>>> => {
    const request = {
        ...filters,
        _page,
        _size,
        closedOffers: true,
    };

    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/loads/locations:paginated`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return { response: response.data };
    } catch {
        return {
            error: new Error(i18n.t('SmartCargas.WonOffers.GetLocationsError')),
        };
    }
};

export const getRoundInfoAsync = async (): Promise<
    IApiResponse<IRoundInfoResponse>
> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get('v2/rounds/round-info');

        if (response.data) {
            return { response: response.data };
        }

        return { error: new Error(i18n.t('ErrorGettingRoundInfo')) };
    } catch (error: any) {
        return handleOffersApiError(error);
    }
};

export const getOffersAvailableV2Async = async (
    filters: IWonFiltersModel,
    page: number,
    pageSize: number = 30,
    position: Position = Position.First,
): Promise<IApiResponse<IOffersAvailableV2Response>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();
        const request = buildOffersRequestParams(page, pageSize, filters, {
            position,
        });

        const response = await Api.get('v2/rounds/loads:available', {
            params: request,
            paramsSerializer(params: any) {
                return stringify(params, { allowDots: true });
            },
        });

        if (response.data) {
            return { response: response.data };
        }

        return { error: new Error(i18n.t('ErrorGettingOffers')) };
    } catch (error: any) {
        return handleOffersApiError(error);
    }
};
